import React, { useState, useEffect } from 'react';
import { getAllBookingsFromFirestore } from '../utils/firestoreBooking';
import './BookingModalAdmin.css';
import { createRoot } from 'react-dom/client';

import { FaTimes } from 'react-icons/fa';
import Invoice from './Invoice';
import DormitoryLayout from '../booking-components/DormitoryLayout';

export default function BookingModalAdmin({ open, mode, booking, onSave, onClose }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    checkInTime: '',
    checkOutTime: '',
    bedType: 'Male Dormitory',
    selectedBeds: [], // [{ id, roomId, position, display }],
    beds: 1,
    pricePerBed: 599,
    applyConvenienceFee: true,
    convenienceFeeRate: 0.05,
    amountPaid: 0,
    amount: 0,
    status: 'pending',
    payment: 'pending',
    extraCharges: [], // [{ label: '', amount: 0 }]
  });

  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  useEffect(() => {
    // Only load initial data once when modal opens, not on every render
    if (open && !initialDataLoaded) {
      if (mode === 'edit' || mode === 'view') {
        console.log('BookingModalAdmin: Loading booking data for', mode, 'mode:', booking);
        console.log('BookingModalAdmin: Original booking payment status:', booking?.payment);
        console.log('BookingModalAdmin: Original booking status:', booking?.status);
        console.log('BookingModalAdmin: Full booking object:', booking);
        
        const formData = {
          name: booking?.guestName || booking?.name || '',
          email: booking?.guestEmail || booking?.email || '',
          phone: booking?.guestPhone || booking?.phone || '',
          checkIn: booking?.checkIn || '',
          checkOut: booking?.checkOut || '',
          checkInTime: booking?.checkInTime || '',
          checkOutTime: booking?.checkOutTime || '',
          bedType: booking?.bedType || 'Male Dormitory',
          selectedBeds: booking?.selectedBeds || [],
          beds: booking?.beds || booking?.selectedBeds?.length || 1,
          pricePerBed: booking?.pricePerBed || booking?.ratePerNight || 599,
          applyConvenienceFee: booking?.applyConvenienceFee !== undefined ? booking.applyConvenienceFee : true,
          convenienceFeeRate: booking?.convenienceFeeRate ?? 0.05,
          amountPaid: booking?.amountPaid || 0,
          amount: booking?.total || booking?.amount || 0,
          status: booking?.status || 'pending',
          payment: booking?.payment || 'pending',
          extraCharges: booking?.extraCharges || [],
        };
        
        console.log('BookingModalAdmin: Form data being set:', formData);
        console.log('BookingModalAdmin: Payment status being set:', formData.payment);
        setForm(formData);
      } else {
        setForm({
          name: '',
          email: '',
          phone: '',
          checkIn: '',
          checkOut: '',
          checkInTime: '',
          checkOutTime: '',
          bedType: 'Male Dormitory',
          selectedBeds: [],
          beds: 1,
          pricePerBed: 599,
          applyConvenienceFee: true,
          convenienceFeeRate: 0.05,
          amountPaid: 0,
          amount: 0,
          status: 'pending',
          payment: 'pending',
          extraCharges: [],
        });
      }
      setInitialDataLoaded(true);
    }
    
    // Reset when modal closes
    if (!open) {
      setInitialDataLoaded(false);
    }
  }, [open, mode, booking, initialDataLoaded]);

  // Find unavailable beds for selected dates
  const [unavailableBedIds, setUnavailableBedIds] = useState([]);

  useEffect(() => {
    // Only run if dates are set
    if (!form.checkIn || !form.checkOut) {
      setUnavailableBedIds([]);
      return;
    }
    
    const fetchBookings = async () => {
      try {
        const allBookings = await getAllBookingsFromFirestore();
        const thisBookingId = booking?.id;
        const checkIn = new Date(form.checkIn);
        const checkOut = new Date(form.checkOut);
        // Overlap: (A < D) && (B > C)
        const overlapping = allBookings.filter(b => {
          if (thisBookingId && b.id === thisBookingId) return false; // skip self in edit mode
          if (!b.checkIn || !b.checkOut) return false;
          const bCheckIn = new Date(b.checkIn);
          const bCheckOut = new Date(b.checkOut);
          return (checkIn < bCheckOut) && (checkOut > bCheckIn);
        });
        // Collect all bed IDs from overlapping bookings
        const bedsBooked = overlapping.flatMap(b => (b.selectedBeds ? b.selectedBeds.map(bed => bed.id) : []));
        setUnavailableBedIds(bedsBooked);
      } catch (error) {
        console.error('Error fetching bookings:', error);
        setUnavailableBedIds([]);
      }
    };
    
    fetchBookings();
  }, [form.checkIn, form.checkOut, booking]);

  if (!open) return null;

  const isView = mode === 'view';

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`BookingModalAdmin: Field ${name} changed to:`, value);
    
    if (name === 'payment') {
      console.log(`BookingModalAdmin: Payment status changed from ${form.payment} to ${value}`);
      console.log('BookingModalAdmin: Current booking object:', booking);
      console.log('BookingModalAdmin: Current booking paymentMethod:', booking?.paymentMethod);
      console.log('BookingModalAdmin: Current booking source:', booking?.source);
      console.log('BookingModalAdmin: Is this a manual booking?', booking?.paymentMethod === 'offline' || booking?.source === 'localStorage');
    }
    
    setForm(prev => {
      const next = { ...prev, [name]: name === 'applyConvenienceFee' ? !!e.target.checked : (name === 'pricePerBed' || name === 'beds' || name === 'amountPaid' ? Number(value) : value) };
      // Trim selected beds if reduced
      if (name === 'beds') {
        const newCount = Math.max(1, Math.min(38, Number(value)));
        next.beds = newCount;
        next.selectedBeds = (prev.selectedBeds || []).slice(0, newCount);
      }
      // Recalculate total when relevant fields change
      if (['checkIn','checkOut','beds','pricePerBed','extraCharges','applyConvenienceFee','convenienceFeeRate'].includes(name)) {
        const checkIn = next.checkIn ? new Date(next.checkIn) : null;
        const checkOut = next.checkOut ? new Date(next.checkOut) : null;
        const nights = (checkIn && checkOut) ? Math.max(1, Math.ceil((checkOut - checkIn) / (1000*60*60*24))) : 1;
        const beds = parseInt(next.beds) || 1;
        const price = Number(next.pricePerBed) || 0;
        const extra = (next.extraCharges||[]).reduce((s,c)=> s + (parseFloat(c.amount)||0), 0);
        const subtotal = price * beds * nights;
        const fee = next.applyConvenienceFee ? Math.round(subtotal * (Number(next.convenienceFeeRate)||0.05)) : 0;
        next.amount = subtotal + extra + fee;
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('BookingModalAdmin: handleSubmit called with mode:', mode);
    console.log('BookingModalAdmin: Form data being saved:', form);
    console.log('BookingModalAdmin: Payment status:', form.payment);
    console.log('BookingModalAdmin: Status:', form.status);
    
    // CRITICAL: Add payment update flag to prevent reversion
    const dataToSave = {
      ...form,
      paymentUpdated: form.payment !== booking?.payment ? new Date().toISOString() : booking?.paymentUpdated,
      lastModified: new Date().toISOString()
    };
    
    console.log('BookingModalAdmin: Data with payment lock:', dataToSave);
    
    if (mode === 'add') {
      // For add mode, we'll save the booking directly
      onSave(dataToSave);
      return;
    }
    if (!form.selectedBeds || form.selectedBeds.length === 0) {
      alert('Please select at least one bed.');
      return;
    }
    onSave(dataToSave);
  }

  function handleDownloadInvoice() {
    // Use current pricePerBed and fee settings
    const bedPrice = Number(form.pricePerBed || 600);
    
    // Calculate nights
    const checkIn = new Date(form.checkIn);
    const checkOut = new Date(form.checkOut);
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    
    // Create booking details object
    const bookingDetails = {
      bookingName: form.name || booking?.name || booking?.guestName || 'Guest',
      guestName: form.name || booking?.name || booking?.guestName || 'Guest',
      bookingId: (booking?.id 
        ? (booking.id.startsWith('SKY') 
            ? booking.id 
            : `SKY${booking.id.padStart(5, '0')}`)
        : (booking?.bookingId 
            ? (booking.bookingId.startsWith('SKY') 
                ? booking.bookingId 
                : `SKY${booking.bookingId.padStart(5, '0')}`)
            : 'SKY00000')
      ),
      guestDetails: {
        name: form.name || booking?.name || booking?.guestName || 'Guest',
        email: form.email || booking?.userEmail || '',
        phone: form.phone || '',
      },
      checkInDate: form.checkIn ? new Date(form.checkIn).toLocaleDateString() : (booking?.checkIn ? new Date(booking.checkIn).toLocaleDateString() : 'N/A'),
      checkOutDate: form.checkOut ? new Date(form.checkOut).toLocaleDateString() : (booking?.checkOut ? new Date(booking.checkOut).toLocaleDateString() : 'N/A'),
      beds: form.beds || booking?.beds || 1,
      guests: booking?.guests || booking?.beds || form.beds || 1,
      nights: nights > 0 ? nights : (booking?.nights || 1),
      bedNumbers: (form.selectedBeds && form.selectedBeds.length > 0)
        ? form.selectedBeds.map(bed => bed.display).join(', ')
        : (Array.isArray(booking?.bedNumbers) && booking.bedNumbers.length > 0
            ? booking.bedNumbers.join(', ')
            : '---'),
      bedType: form.bedType || booking?.bedType || 'Dormitory',
      ratePerNight: bedPrice || booking?.ratePerNight || 600,
      taxRate: 0, // No tax, only convenience fee
      extraCharges: form.extraCharges || booking?.extraCharges || [],
      checkInTime: booking?.checkInTime,
      checkOutTime: booking?.checkOutTime,
      packageInfo: booking?.selectedPackage || booking?.packageInfo,
      totalAmount: form.amount || booking?.amount || booking?.total || 0,
      applyConvenienceFee: form.applyConvenienceFee,
      convenienceFeeRate: form.convenienceFeeRate,
      amountPaid: form.amountPaid || 0,
    };
    
    // Create a new window for the invoice
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${bookingDetails.bookingId}</title>
          <link rel="stylesheet" type="text/css" href="/src/components/Invoice.css" />
        </head>
        <body>
          <div id="invoice-root"></div>
          <script>
            window.bookingDetails = ${JSON.stringify(bookingDetails)};
          </script>
          <script crossorigin src="https://unpkg.com/react@18/umd/react.development.js"></script>
          <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
          <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
          <script type="text/babel">
            const Invoice = ${Invoice.toString()};
            const root = ReactDOM.createRoot(document.getElementById('invoice-root'));
            root.render(<Invoice bookingDetails={window.bookingDetails} />);
            setTimeout(() => { window.print(); window.close(); }, 500);
          </script>
        </body>
      </html>
    `);
  }

  return (
    <div className="modal-overlay">
      <div className="booking-modal">
        <button className="modal-close" onClick={onClose} aria-label="Close modal">
          <FaTimes />
        </button>
        <div className="modal-content">
          {/* Left side - Form */}
          <div className="modal-form-section">
            <div className="modal-title">
              {mode==='add'?'Add Booking':mode==='edit'?'Edit Booking':'Booking Details'}
            </div>
            <form onSubmit={handleSubmit} className="booking-form">
              <div>
                <label htmlFor="name">Name</label>
                <input 
                  id="name"
                  className="modal-input" 
                  name="name" 
                  value={form.name||''} 
                  onChange={handleChange} 
                  disabled={isView} 
                  required 
                />
              </div>
              <div>
                <label htmlFor="email">Email</label>
                <input 
                  id="email"
                  className="modal-input" 
                  name="email" 
                  value={form.email||''} 
                  onChange={handleChange} 
                  disabled={isView} 
                  required 
                />
              </div>
              <div>
                <label htmlFor="phone">Phone</label>
                <input 
                  id="phone"
                  className="modal-input" 
                  name="phone" 
                  value={form.phone||''} 
                  onChange={handleChange} 
                  disabled={isView} 
                  required 
                  pattern="[0-9]{10,15}"
                  title="Please enter a valid phone number"
                />
              </div>
              <div className="modal-row">
                <div className="modal-col">
                  <label htmlFor="checkIn">Check-In Date</label>
                  <input 
                    id="checkIn"
                    className="modal-input" 
                    type="date" 
                    name="checkIn" 
                    value={form.checkIn||''} 
                    onChange={handleChange} 
                    disabled={isView} 
                    required 
                  />
                </div>
                <div className="modal-col">
                  <label htmlFor="checkOut">Check-Out Date</label>
                  <input 
                    id="checkOut"
                    className="modal-input" 
                    type="date" 
                    name="checkOut" 
                    value={form.checkOut||''} 
                    onChange={handleChange} 
                    disabled={isView} 
                    required 
                  />
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-col">
                  <label htmlFor="pricePerBed">Price Per Bed (₹)</label>
                  <input
                    id="pricePerBed"
                    className="modal-input"
                    type="number"
                    min={0}
                    step={1}
                    name="pricePerBed"
                    value={form.pricePerBed || 0}
                    onChange={handleChange}
                    disabled={isView}
                    required
                  />
                </div>
                <div className="modal-col" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <label htmlFor="applyConvenienceFee" style={{ margin: 0 }}>Apply Convenience Fee (5%)</label>
                  <input
                    id="applyConvenienceFee"
                    type="checkbox"
                    name="applyConvenienceFee"
                    checked={!!form.applyConvenienceFee}
                    onChange={handleChange}
                    disabled={isView}
                  />
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-col">
                  <label htmlFor="checkInTime">Check-In Time</label>
                  <input 
                    id="checkInTime"
                    className="modal-input" 
                    type="time" 
                    name="checkInTime" 
                    value={form.checkInTime||''} 
                    onChange={handleChange} 
                    disabled={isView} 
                  />
                </div>
                <div className="modal-col">
                  <label htmlFor="checkOutTime">Check-Out Time</label>
                  <input 
                    id="checkOutTime"
                    className="modal-input" 
                    type="time" 
                    name="checkOutTime" 
                    value={form.checkOutTime||''} 
                    onChange={handleChange} 
                    disabled={isView} 
                  />
                </div>
              </div>
              <div className="modal-row">
  <div className="modal-col">
    <label htmlFor="beds">Number of Beds</label>
    <input
      id="beds"
      className="modal-input"
      name="beds"
      type="number"
      min={1}
      max={38}
      value={form.beds || 1}
      onChange={handleChange}
      disabled={isView}
      required
    />
  </div>
  <div className="modal-col">
    <label htmlFor="bedType">Bed Type</label>
    <select 
      id="bedType"
      className="modal-input" 
      name="bedType" 
      value={form.bedType||''} 
      onChange={handleChange} 
      disabled={isView} 
      required 
    >
      <option value="Male Dormitory">Male Dormitory</option>
      <option value="Female Dormitory">Female Dormitory</option>
      <option value="Mixed Dormitory">Mixed Dormitory</option>
    </select>
  </div>
                <div className="modal-col">
                  <label>Bed Selection</label>
                  {!isView && (
                    <DormitoryLayout
  bedsToSelect={form.beds || 1}
  selectedBeds={form.selectedBeds || []}
  unavailableBeds={unavailableBedIds}
  onConfirm={beds => setForm(f => ({ ...f, selectedBeds: beds }))}
  onCancel={() => {}}
/>
                  )}
                  {isView && (
                    <div>
                      {form.selectedBeds && form.selectedBeds.length > 0
                        ? form.selectedBeds.map(bed => (
                            <div key={bed.id}>{bed.display}</div>
                          ))
                        : <span>No beds selected</span>}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-col">
                  <label htmlFor="status">Status</label>
                  <select 
                    id="status"
                    className="modal-input" 
                    name="status" 
                    value={form.status||''} 
                    onChange={handleChange} 
                    disabled={isView} 
                    required 
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="checked-in">Checked In</option>
                    <option value="checked-out">Checked Out</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="modal-col">
                  <label htmlFor="payment">Payment</label>
                  <select 
                    id="payment"
                    className="modal-input" 
                    name="payment" 
                    value={form.payment||''} 
                    onChange={handleChange} 
                    disabled={isView} 
                    required 
                  >
                    <option value="pending">Pending</option>
                    <option value="partial">Partial</option>
                    <option value="paid">Paid</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="amount">Amount (₹)</label>
                <input 
                  id="amount"
                  className="modal-input" 
                  type="number" 
                  min={0} 
                  step={0.01} 
                  name="amount" 
                  value={form.amount||''} 
                  onChange={handleChange} 
                  disabled={isView} 
                  required 
                />
              </div>
              {form.payment === 'partial' && (
                <div>
                  <label htmlFor="amountPaid">Amount Paid (₹)</label>
                  <input
                    id="amountPaid"
                    className="modal-input"
                    type="number"
                    min={0}
                    step={0.01}
                    name="amountPaid"
                    value={form.amountPaid || 0}
                    onChange={handleChange}
                    disabled={isView}
                    required
                  />
                </div>
              )}
              {/* Extra Charges Section */}
              <div>
                <label>Extra Charges</label>
                {form.extraCharges && form.extraCharges.length > 0 && form.extraCharges.map((charge, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                    <input
                      type="text"
                      placeholder="Description (e.g. Towel)"
                      className="modal-input"
                      style={{ flex: 2 }}
                      value={charge.label}
                      onChange={e => {
                        if (isView) return;
                        const updated = [...form.extraCharges];
                        updated[idx].label = e.target.value;
                        setForm(f => ({ ...f, extraCharges: updated }));
                      }}
                      disabled={isView}
                      required
                    />
                    <input
                      type="number"
                      placeholder="Amount"
                      className="modal-input"
                      style={{ flex: 1 }}
                      value={charge.amount}
                      min={0}
                      step={0.01}
                      onChange={e => {
                        if (isView) return;
                        const updated = [...form.extraCharges];
                        updated[idx].amount = parseFloat(e.target.value) || 0;
                        setForm(f => ({ ...f, extraCharges: updated }));
                      }}
                      disabled={isView}
                      required
                    />
                    {!isView && (
                      <button type="button" className="modal-btn modal-btn-secondary" style={{ marginLeft: 2, padding: '0.35rem 0.9rem', fontSize: '0.95rem', height: '2.2rem' }} onClick={() => {
                        setForm(f => ({ ...f, extraCharges: f.extraCharges.filter((_, i) => i !== idx) }));
                      }}>Remove</button>
                    )}
                  </div>
                ))}
                {!isView && (
                  <button type="button" className="modal-btn modal-btn-secondary" onClick={() => setForm(f => ({ ...f, extraCharges: [...(f.extraCharges||[]), { label: '', amount: 0 }] }))} style={{ marginTop: 4, fontSize: 13, padding: '0.35rem 0.9rem', height: '2.2rem' }}>+ Add Extra Charge</button>
                )}
              </div>

              
              {/* Action buttons - only show in add/edit mode */}
              {!isView && (
                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="modal-btn modal-btn-secondary" 
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="modal-btn modal-btn-primary"
                  >
                    {mode === 'add' ? 'Add Booking' : 'Update Booking'}
                  </button>
                </div>
              )}
              
              {/* View mode buttons */}
              {isView && (
                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="modal-btn modal-btn-secondary" 
                    onClick={handleDownloadInvoice}
                  >
                    Download Invoice
                  </button>
                  <button 
                    type="button" 
                    className="modal-btn modal-btn-secondary" 
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              )}
            </form>
          </div>
          
          {/* Add/Edit mode - Booking preview */}
          {(!isView) && (
            <div className="modal-summary-section">
              <div className="modal-summary-title">
                Booking Preview
              </div>
              <div className="modal-summary-content">
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Guest:</span>
                  <span className="modal-summary-value">{form.name || '---'}</span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Email:</span>
                  <span className="modal-summary-value">{form.email || '---'}</span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Phone:</span>
                  <span className="modal-summary-value">{form.phone || '---'}</span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Dates:</span>
                  <span className="modal-summary-value modal-summary-value-right">
                    {form.checkIn ? new Date(form.checkIn).toLocaleDateString() : '---'}
                    {form.checkInTime && ` at ${form.checkInTime}`}<br/>
                    to {form.checkOut ? new Date(form.checkOut).toLocaleDateString() : '---'}
                    {form.checkOutTime && ` at ${form.checkOutTime}`}
                  </span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Bed Details:</span>
                  <span className="modal-summary-value modal-summary-value-right">
                    {form.beds || 0} {form.bedType || '---'} bed{form.beds !== 1 ? 's' : ''}<br/>
                    {form.selectedBeds && form.selectedBeds.length > 0
                      ? <span style={{fontSize:'0.98em',color:'#232d4d'}}>
                          Beds: {form.selectedBeds.length >= 38 ? 'All beds' : form.selectedBeds.map(bed => bed.display).join(', ')}
                        </span>
                      : <span style={{fontSize:'0.98em',color:'#b91c1c'}}>No beds selected</span>
                    }
                  </span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Status:</span>
                  <span className="modal-summary-value">{form.status || '---'}</span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Payment:</span>
                  <span className="modal-summary-value">{form.payment || '---'}</span>
                </div>
                <div className="modal-summary-total">
                  <span className="modal-summary-total-label">Total:</span>
                  <span className="modal-summary-total-value">₹{form.amount || 0}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* View mode - Full details */}
          {isView && (
            <div className="modal-summary-section">
              <div className="modal-summary-title">
                Booking Details
              </div>
              <div className="modal-summary-content">
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Booking ID:</span>
                  <span className="modal-summary-value">{booking?.id ? (booking.id.startsWith('SKY') ? booking.id : `SKY${booking.id.padStart(5, '0')}`) : 'SKY00000'}</span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Guest:</span>
                  <span className="modal-summary-value">{form.name || '---'}</span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Email:</span>
                  <span className="modal-summary-value">{form.email || '---'}</span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Phone:</span>
                  <span className="modal-summary-value">{form.phone || '---'}</span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Dates:</span>
                  <span className="modal-summary-value modal-summary-value-right">
                    {form.checkIn ? new Date(form.checkIn).toLocaleDateString() : '---'}<br/>
                    to {form.checkOut ? new Date(form.checkOut).toLocaleDateString() : '---'}
                  </span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Bed Details:</span>
                  <span className="modal-summary-value modal-summary-value-right">
                    {form.beds || 0} {form.bedType || '---'} bed{form.beds !== 1 ? 's' : ''}<br/>
                    {form.selectedBeds && form.selectedBeds.length > 0
                      ? <span style={{fontSize:'0.98em',color:'#232d4d'}}>
                          Beds: {form.selectedBeds.map(bed => bed.display).join(', ')}
                        </span>
                      : <span style={{fontSize:'0.98em',color:'#b91c1c'}}>No beds selected</span>
                    }
                  </span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Status:</span>
                  <span className="modal-summary-value">{form.status || '---'}</span>
                </div>
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Payment:</span>
                  <span className="modal-summary-value">{form.payment || '---'}</span>
                </div>
                <div className="modal-summary-total">
                  <span className="modal-summary-total-label">Total:</span>
                  <span className="modal-summary-total-value">₹{form.amount || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
